import { useEffect, useMemo, useState } from "react";
import { ArrowRight, CreditCard, Download, MessageCircle, Zap } from "lucide-react";
import { PageHeader, StatCard, StatusPill } from "../components/Primitives";
import { Modal } from "../components/Modal";
import { api, errorText, inr, formatDate } from "../lib/api";
import { buildWhatsAppLink } from "../components/WhatsAppBubble";

const STATUSES = ["ALL", "UPCOMING", "PENDING", "PARTIAL", "OVERDUE", "PAID"];

export default function Bills() {
  const [bills, setBills] = useState([]);
  const [status, setStatus] = useState("ALL");
  const [error, setError] = useState("");
  const [running, setRunning] = useState(false);
  const [payFor, setPayFor] = useState(null);
  const [payForm, setPayForm] = useState({ amount: "", payment_mode: "CASH" });

  const load = async () => {
    try {
      const res = await api.get("/bills");
      setBills(res.data.data);
    } catch (err) {
      setError(errorText(err));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => (status === "ALL" ? bills : bills.filter((bill) => bill.status === status)), [bills, status]);
  const metrics = useMemo(() => ({
    pending: bills.filter((b) => b.status === "PENDING").length,
    overdue: bills.filter((b) => b.status === "OVERDUE").length,
    paid: bills.filter((b) => b.status === "PAID").length,
    upcoming: bills.filter((b) => b.status === "UPCOMING").length,
  }), [bills]);

  const runNow = async () => {
    setRunning(true);
    try {
      await api.post("/bills/run");
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setRunning(false);
    }
  };

  const paySubmit = async (event) => {
    event.preventDefault();
    try {
      await api.post("/payments", {
        student_id: payFor.student_id,
        amount: Number(payForm.amount),
        payment_mode: payForm.payment_mode,
        payment_for: "MONTHLY_FEE",
        bill_id: payFor.id,
      });
      setPayFor(null);
      setPayForm({ amount: "", payment_mode: "CASH" });
      load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const exportBills = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/reports/export/bills`, "_blank");
  };

  return (
    <div className="page-enter">
      <PageHeader
        eyebrow="FEE OPERATIONS"
        title="Bills"
        description="Every month's dues auto-generate — collect, mark paid or re-run the engine."
        action={
          <div style={{ display: "flex", gap: 10 }}>
            <button className="button button-outline" onClick={exportBills} data-testid="export-bills-button"><Download size={16} /> Export</button>
            <button className="button button-primary" onClick={runNow} disabled={running} data-testid="run-bills-button">
              <Zap size={16} /> {running ? "Running…" : "Run billing now"}
            </button>
          </div>
        }
      />
      {error ? <div className="error-message">{error}</div> : null}
      <div className="stat-grid">
        <StatCard label="Pending" value={metrics.pending} detail="Awaiting payment" icon={<CreditCard size={18} />} tone="orange" />
        <StatCard label="Overdue" value={metrics.overdue} detail="Past due date" icon={<CreditCard size={18} />} tone="dark" />
        <StatCard label="Upcoming" value={metrics.upcoming} detail="Scheduled" icon={<CreditCard size={18} />} />
        <StatCard label="Paid" value={metrics.paid} detail="Settled" tone="green" icon={<CreditCard size={18} />} />
      </div>
      <section className="content-card table-card">
        <div className="table-toolbar">
          <div>
            <span className="card-kicker">LEDGER</span>
            <h2>{filtered.length} bills</h2>
          </div>
          <select value={status} onChange={(event) => setStatus(event.target.value)} data-testid="bills-filter">
            {STATUSES.map((item) => (<option key={item} value={item}>{item}</option>))}
          </select>
        </div>
        <div className="table-wrap">
          <table data-testid="bills-table">
            <thead>
              <tr><th>Student</th><th>Period</th><th>Amount</th><th>Paid</th><th>Balance</th><th>Due</th><th>Status</th><th>Action</th></tr>
            </thead>
            <tbody>
              {filtered.map((bill) => (
                <tr key={bill.id} data-testid={`bill-row-${bill.id}`}>
                  <td><strong>{bill.student_name}</strong><br /><small style={{ color: "#64748b" }}>{bill.student_code}</small></td>
                  <td>{bill.period_label}</td>
                  <td>{inr(bill.amount)}</td>
                  <td>{inr(bill.paid_amount)}</td>
                  <td>{inr(bill.balance)}</td>
                  <td>{formatDate(bill.due_date)}</td>
                  <td><StatusPill status={bill.status} /></td>
                  <td>
                    <div className="action-row">
                      {bill.balance > 0 ? (
                        <button className="button button-outline button-small" onClick={() => { setPayFor(bill); setPayForm({ amount: bill.balance, payment_mode: "CASH" }); }} data-testid={`collect-bill-${bill.id}`}>
                          Collect
                        </button>
                      ) : (
                        <span style={{ color: "#16a34a", fontSize: 12 }}>Settled</span>
                      )}
                      {bill.balance > 0 && bill.student_mobile ? (
                        <a
                          className="button button-whatsapp button-small"
                          href={buildWhatsAppLink(
                            bill.student_mobile,
                            `Hi ${bill.student_name || ""}, your ${bill.period_label} bill of ${inr(bill.balance)} is due on ${formatDate(bill.due_date)}. Kindly settle it at the library. Thanks!`
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`whatsapp-bill-${bill.id}`}
                        >
                          <MessageCircle size={13} /> Remind
                        </a>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {payFor ? (
        <Modal title={`Collect for ${payFor.student_name}`} eyebrow={payFor.period_label} close={() => setPayFor(null)} testId="collect-bill-modal">
          <form className="modal-form" onSubmit={paySubmit}>
            <p style={{ color: "#64748b" }}>Balance due: <strong>{inr(payFor.balance)}</strong></p>
            <label>Amount<input type="number" min="1" required value={payForm.amount} onChange={(event) => setPayForm({ ...payForm, amount: event.target.value })} data-testid="collect-amount-input" /></label>
            <label>Mode
              <select value={payForm.payment_mode} onChange={(event) => setPayForm({ ...payForm, payment_mode: event.target.value })}>
                <option value="CASH">Cash</option>
                <option value="UPI">UPI</option>
                <option value="BANK_TRANSFER">Bank transfer</option>
                <option value="CARD">Card</option>
              </select>
            </label>
            <div className="modal-actions">
              <button type="button" className="button button-outline" onClick={() => setPayFor(null)}>Cancel</button>
              <button className="button button-primary" type="submit" data-testid="collect-bill-save">Record payment <ArrowRight size={16} /></button>
            </div>
          </form>
        </Modal>
      ) : null}
    </div>
  );
}
